<template>
  <div class="ticket-create-container">
    <!-- Header Section -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-icon">
            <feather-icon
              icon="Edit3Icon"
              size="28"
              aria-label="Créer un ticket de suivi"
            />
          </div>
          <div class="header-text">
            <h1 class="page-title">
              Créer un ticket de suivi
            </h1>
            <p class="page-subtitle">
              Merci de remplir le formulaire pour ouvrir une demande de support liée à vos colis.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="form-container">
      <b-card class="form-card">
        <b-form
          novalidate
          class="ticket-create-form"
          aria-label="Formulaire de création de ticket de support logistique"
          @submit.prevent="submit"
        >
          <div class="form-section">
            <div class="section-title">
              <feather-icon
                icon="SettingsIcon"
                size="18"
                class="section-icon"
              />
              <span>Informations du ticket</span>
            </div>

            <!-- Catégorie -->
            <b-form-group
              label="Catégorie du ticket"
              label-for="category"
              label-class="form-label-custom"
              :state="categoryState"
              class="form-group-custom"
            >
              <template #description>
                <span class="field-description">Sélectionnez la catégorie la plus appropriée. <span class="required-indicator">*</span></span>
              </template>
              <div class="input-wrapper">
                <feather-icon
                  icon="TagIcon"
                  size="18"
                  class="input-icon"
                />
                <v-select
                  id="category"
                  v-model="form.category"
                  :options="categoryOptions"
                  :reduce="cat => cat.value"
                  :get-option-label="option => option.label"
                  :clearable="false"
                  placeholder="Sélectionner la catégorie"
                  :class="['custom-select-input', { 'is-invalid': categoryState === false }]"
                  aria-required="true"
                  @input="onCategoryChange"
                />
              </div>
              <b-form-invalid-feedback>
                La catégorie est requise.
              </b-form-invalid-feedback>
              <div
                v-if="selectedCategoryDescription"
                class="category-info-box"
              >
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                  class="info-icon"
                />
                <span>{{ selectedCategoryDescription }}</span>
              </div>
            </b-form-group>

            <!-- Titre -->
            <b-form-group
              label="Titre du ticket"
              label-for="title"
              label-class="form-label-custom"
              :state="titleState"
              class="form-group-custom"
            >
              <template #description>
                <span class="field-description">Un titre court et explicite pour votre demande de suivi (optionnel).</span>
              </template>
              <div class="input-wrapper">
                <feather-icon
                  icon="TypeIcon"
                  size="18"
                  class="input-icon"
                />
                <b-form-input
                  id="title"
                  v-model.trim="form.title"
                  :state="titleState"
                  placeholder="Saisir le titre du ticket de suivi"
                  maxlength="120"
                  autocomplete="off"
                  class="custom-input"
                />
              </div>
            </b-form-group>
          </div>

          <div class="form-section">
            <div class="section-title">
              <feather-icon
                icon="FileTextIcon"
                size="18"
                class="section-icon"
              />
              <span>Détails de la demande</span>
            </div>

            <!-- Description -->
            <b-form-group
              label="Description du problème"
              label-for="description"
              label-class="form-label-custom"
              :state="descState"
              class="form-group-custom"
            >
              <template #description>
                <span class="field-description">Décrivez précisément le problème ou la demande de suivi (optionnel).</span>
              </template>
              <div class="editor-wrapper">
                <vue-editor
                  id="description"
                  v-model="form.description"
                  :state="descState"
                  placeholder="Décrire le problème ou la demande de suivi"
                  :editor-toolbar="editorToolbar"
                  class="custom-editor"
                />
              </div>
            </b-form-group>

            <!-- Colis liés -->
            <b-form-group
              label="Colis liés"
              label-for="orders"
              label-class="form-label-custom"
              :state="ordersState"
              class="form-group-custom"
            >
              <template #description>
                <span class="field-description">Sélectionnez un ou plusieurs colis concernés par ce ticket de suivi.</span>
              </template>
              <div class="input-wrapper">
                <feather-icon
                  icon="PackageIcon"
                  size="18"
                  class="input-icon"
                />
                <v-select
                  id="orders"
                  v-model="form.orders"
                  :options="orderOptions"
                  :reduce="order => order.value"
                  :get-option-label="option => option.label"
                  :multiple="true"
                  :taggable="false"
                  :close-on-select="false"
                  :clearable="true"
                  placeholder="Sélectionner les colis"
                  :class="['custom-select-input', { 'is-invalid': ordersState === false }]"
                  label="label"
                >
                  <template #option="option">
                    <span>{{ option.label }}</span>
                  </template>
                  <template #selected-option="option">
                    <span>{{ option.label }}</span>
                  </template>
                </v-select>
              </div>
              <b-form-invalid-feedback>
                Veuillez sélectionner au moins un colis.
              </b-form-invalid-feedback>
            </b-form-group>
          </div>

          <!-- Submit Section -->
          <div class="submit-section">
            <b-button
              type="submit"
              class="submit-btn"
              :disabled="!formIsValid || loading"
            >
              <b-spinner
                v-if="loading"
                small
                class="mr-2"
              />
              <feather-icon
                v-else
                icon="SendIcon"
                size="18"
                class="mr-2"
              />
              Créer le ticket de suivi
            </b-button>
            <p class="submit-hint">
              Vous recevrez une notification après la création du ticket.
            </p>
          </div>

          <!-- Loading Overlay -->
          <div
            v-if="loading"
            class="loading-overlay"
          >
            <b-spinner
              variant="warning"
              label="Chargement..."
            />
          </div>

          <!-- Success Feedback -->
          <transition name="fade">
            <div
              v-if="showSuccess"
              class="success-message"
            >
              <feather-icon
                icon="CheckCircleIcon"
                class="mr-2"
                size="22"
              />
              Ticket créé avec succès !
            </div>
          </transition>
        </b-form>
      </b-card>
    </div>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
  BFormInvalidFeedback,
  BSpinner,
} from 'bootstrap-vue'
import { VueEditor } from 'vue2-editor'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import 'vue2-editor/dist/vue2-editor.css'

export default {
  components: {
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BFormInvalidFeedback,
    BSpinner,
    VueEditor,
    vSelect,
  },
  data() {
    return {
      form: {
        title: '',
        description: '',
        category: null,
        orders: [],
      },
      categories: [],
      orders: [],
      loading: false,
      showSuccess: false,
      editorToolbar: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
        ['clean'],
      ],
      touched: {
        title: false,
        description: false,
        category: false,
        orders: false,
      },
      selectedCategoryDescription: '',
    }
  },
  computed: {
    categoryOptions() {
      return this.categories.map(cat => ({
        value: cat.id,
        label: cat.display_name || cat.name,
        description: cat.description,
      }))
    },
    orderOptions() {
      return this.orders.map(order => ({
        value: order.id,
        label: `#${order.id} - ${order.name || order.id}`,
      }))
    },
    titleState() {
      // Title is optional, so always return null (no validation)
      return null
    },
    descState() {
      // Description is optional, so always return null (no validation)
      return null
    },
    categoryState() {
      // Only category is required
      return this.touched.category ? !!this.form.category : null
    },
    ordersState() {
      // Orders are optional
      return null
    },
    formIsValid() {
      // Only category is required
      return !!this.form.category
    },
  },
  watch: {
    'form.title': function handleTitle() { this.touched.title = true },
    'form.description': function handleDescription() { this.touched.description = true },
    'form.category': function handleCategory() { this.touched.category = true },
    'form.orders': function handleOrders() { this.touched.orders = true },
  },
  async mounted() {
    await this.fetchCategories()
    await this.fetchOrders()
  },
  methods: {
    async fetchCategories() {
      const { data } = await axios.get('/api/support/categories/')
      this.categories = data
    },
    async fetchOrders() {
      const { data } = await axios.get('/api/orders/eligible-for-ticket/')
      this.orders = data
    },
    async submit() {
      // Mark all as touched for validation
      this.touched.category = true
      if (!this.formIsValid) return

      this.loading = true
      this.showSuccess = false
      try {
        const payload = {
          category: this.form.category,
        }
        // Only include optional fields if filled
        if (this.form.title) payload.title = this.form.title
        if (this.form.description) payload.description = this.form.description
        if (this.form.orders && this.form.orders.length > 0) payload.order_ids = this.form.orders

        // Await the ticket creation and get the response
        const { data } = await axios.post('/api/support/tickets/', payload)
        this.showSuccess = true
        setTimeout(() => {
          this.showSuccess = false
          // Redirect to detail page with the new ticket id
          this.$router.push({ name: 'SupportTicketDetail', params: { id: data.id } })
        }, 1200)
        this.$bvToast.toast('Ticket créé avec succès.', {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })
      } catch (e) {
        this.$bvToast.toast('Erreur lors de la création du ticket.', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
      }
    },
    onCategoryChange(categoryId) {
      const selected = this.categories.find(cat => cat.id === categoryId)
      this.selectedCategoryDescription = selected && selected.description ? selected.description : ''
    },
  },
}
</script>

<style scoped>
/* Container */
.ticket-create-container {
  min-height: 100vh;
  background: #ffffff;
  padding: 0;
}

/* Header Section */
.page-header {
  background: linear-gradient(135deg, rgba(237, 25, 25, 0.12) 0%, rgba(237, 25, 25, 0.08) 100%);
  padding: 2rem 2.5rem;
  margin-bottom: 0;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.header-icon {
  width: 56px;
  height: 56px;
  background: transparent;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1d1d1b;
  box-shadow: none;
  border: none;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1d1d1b;
  margin: 0;
  letter-spacing: -0.5px;
}

.page-subtitle {
  color: #4b5563;
  font-size: 0.95rem;
  margin: 0.25rem 0 0 0;
}

/* Form Container */
.form-container {
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
}

.form-card {
  background: white;
  border-radius: 0;
  border: none;
  box-shadow: none;
  overflow: hidden;
}

.form-card >>> .card-body {
  padding: 2.5rem 3rem;
}

/* Form Sections */
.form-section {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #f1f5f9;
}

.form-section:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1d1d1b;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #ed1919;
  width: fit-content;
}

.section-icon {
  color: #ed1919;
}

/* Form Groups */
.form-group-custom {
  margin-bottom: 1.75rem;
}

.form-label-custom {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
}

.field-description {
  color: #64748b;
  font-size: 0.85rem;
}

.required-indicator {
  color: #ed1919;
  font-weight: 700;
}

/* Input Wrapper */
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 1rem;
  z-index: 2;
  color: #94a3b8;
  transition: color 0.2s;
}

.input-wrapper:focus-within .input-icon {
  color: #ed1919;
}

/* Custom Input */
.custom-input {
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 3rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.95rem;
  background: #f8fafc;
  transition: all 0.2s ease;
}

.custom-input:focus {
  border-color: #ed1919;
  background: white;
  box-shadow: 0 0 0 4px rgba(237, 25, 25, 0.12);
  outline: none;
}

.custom-input::placeholder {
  color: #94a3b8;
}

/* Custom Select */
.custom-select-input {
  width: 100%;
}

.custom-select-input >>> .vs__dropdown-toggle {
  padding: 0.5rem 1rem 0.5rem 3rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  min-height: 50px;
  transition: all 0.2s ease;
}

.custom-select-input >>> .vs__dropdown-toggle:focus-within {
  border-color: #ed1919;
  background: white;
  box-shadow: 0 0 0 4px rgba(237, 25, 25, 0.12);
}

.custom-select-input >>> .vs__selected {
  background: linear-gradient(135deg, #ed1919 0%, #b91c1c 100%);
  color: white;
  border-radius: 8px;
  padding: 0.25rem 0.75rem;
  margin: 0.25rem;
  font-weight: 500;
  border: none;
}

.custom-select-input >>> .vs__deselect {
  fill: white;
}

.custom-select-input >>> .vs__dropdown-menu {
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  padding: 0.5rem;
  margin-top: 0.5rem;
}

.custom-select-input >>> .vs__dropdown-option {
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 0.25rem;
  transition: all 0.15s;
}

.custom-select-input >>> .vs__dropdown-option--highlight {
  background: linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%);
  color: #1d1d1b;
}

.custom-select-input >>> .vs__dropdown-option--selected {
  background: linear-gradient(135deg, #ed1919 0%, #b91c1c 100%);
  color: white;
}

.custom-select-input >>> .vs__search::placeholder {
  color: #94a3b8;
}

/* Editor Wrapper */
.editor-wrapper {
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  background: #f8fafc;
  transition: all 0.2s ease;
}

.editor-wrapper:focus-within {
  border-color: #ed1919;
  background: white;
  box-shadow: 0 0 0 4px rgba(237, 25, 25, 0.12);
}

.custom-editor {
  min-height: 180px;
}

.custom-editor >>> .ql-toolbar {
  border: none;
  border-bottom: 1px solid #e2e8f0;
  background: white;
}

.custom-editor >>> .ql-container {
  border: none;
  font-size: 0.95rem;
}

.custom-editor >>> .ql-editor {
  min-height: 150px;
  padding: 1rem;
}

.custom-editor >>> .ql-editor.ql-blank::before {
  color: #94a3b8;
  font-style: normal;
}

/* Category Info Box */
.category-info-box {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%);
  border-left: 3px solid #ed1919;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #1e293b;
}

.info-icon {
  color: #ed1919;
  flex-shrink: 0;
  margin-top: 2px;
}

/* Submit Section */
.submit-section {
  text-align: center;
  padding-top: 1.5rem;
  border-top: 1px solid #f1f5f9;
  margin-top: 1.5rem;
}

.submit-btn {
  background: linear-gradient(135deg, #ed1919 0%, #b91c1c 100%);
  border: none;
  color: white;
  font-weight: 600;
  font-size: 1.05rem;
  padding: 1rem 2.5rem;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(237, 25, 25, 0.32);
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(237, 25, 25, 0.4);
  background: linear-gradient(135deg, #b91c1c 0%, #ed1919 100%);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-hint {
  color: #94a3b8;
  font-size: 0.85rem;
  margin-top: 1rem;
  margin-bottom: 0;
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  z-index: 10;
}

/* Success Message */
.success-message {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #059669;
  font-size: 1.1rem;
  font-weight: 600;
  margin-top: 1.5rem;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(5, 150, 105, 0.1);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .page-header {
    padding: 1.5rem 1rem;
  }

  .header-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .page-title {
    font-size: 1.35rem;
  }

  .form-card >>> .card-body {
    padding: 1.5rem;
  }

  .submit-btn {
    width: 100%;
  }
}
</style>
